import React from "react";
import { Box, IconButton, Badge, Tooltip } from "@chakra-ui/react";
import { FiBell } from "react-icons/fi";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { GET_UNREAD_NOTIFICATION_COUNT } from "../pages/notifications/queries";

/**
 * The navbar bell — the entry point to the notifications module's inbox.
 * Lives in src/components (not the module's pages) because it's app chrome
 * rendered on every page, same as the navbar it sits in.
 *
 * Polls the unread count every 60s (indexed count query — cheap). The badge
 * caps at 99+ so a backlog doesn't blow out the navbar.
 */
export const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { data } = useQuery(GET_UNREAD_NOTIFICATION_COUNT, {
    skip: !isAuthenticated,
    pollInterval: 60000,
    fetchPolicy: "cache-and-network",
  });

  if (!isAuthenticated) return null;
  const count: number = data?.unreadNotificationCount ?? 0;

  return (
    <Tooltip
      hasArrow
      openDelay={300}
      label={count > 0 ? `${count} unread notification${count !== 1 ? "s" : ""} — tap to open your inbox` : "Notifications — updates, approvals and reminders land here"}
    >
      <Box position="relative" display="inline-block">
        <IconButton
          aria-label={`Notifications${count > 0 ? ` (${count} unread)` : ""}`}
          icon={<FiBell />}
          size="md" minH="44px" minW="44px"
          variant="ghost"
          onClick={() => navigate("/notifications")}
        />
        {count > 0 && (
          <Badge
            position="absolute"
            top="-2px"
            right="-4px"
            colorScheme="red"
            variant="solid"
            borderRadius="full"
            fontSize="sm" lineHeight="1"
            px={1.5}
            pointerEvents="none"
          >
            {count > 99 ? "99+" : count}
          </Badge>
        )}
      </Box>
    </Tooltip>
  );
};
